import { useEffect, useMemo, useState } from 'react';
import { ErrorState, LoadingState } from '@beautyburst/ui';
import { App, type RetailerActor } from './App.tsx';
import { createRetailerHttpClient, RetailerApiError } from './http-client.ts';
import { resolveRoute } from './routes.ts';
import {
  CatalogScreen,
  InventoryScreen,
  ImportsScreen,
  OrderDetailScreen,
  OrdersScreen,
  DashboardScreen,
  StoreProfileScreen,
  AfterHoursScreen,
  PayoutsScreen,
} from './screens.tsx';
import { MfaStepUpDialog, RetailerSecurityScreen, useMfaStepUpChallenge } from './mfa.tsx';
import type {
  AfterHoursEnrollment,
  RetailerBootstrap,
  RetailerDashboard,
  RetailerOrderSummary,
  RetailerPayoutOverview,
  RetailerStoreProfile,
} from './api-client.ts';
import { retailerHref } from './base-path.ts';
import { RETAILER_ROUTES } from './routes.ts';

type RemoteState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; value: T; screen?: string }
  | { status: 'failed'; error: RetailerApiError | Error };

function failure(error: unknown): RetailerApiError | Error {
  return error instanceof Error ? error : new Error('The request could not be completed.');
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function text(value: unknown, fallback = '—'): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

function orderSummary(value: unknown): RetailerOrderSummary {
  const row = record(value);
  return {
    id: text(row.id),
    sub_order_id: text(row.sub_order_id),
    order_id: text(row.order_id),
    location_id: text(row.location_id),
    status: text(row.status, 'PENDING') as RetailerOrderSummary['status'],
    fulfillment_mode: text(row.fulfillment_mode),
    created_at: text(row.created_at, new Date(0).toISOString()),
  };
}

function firstPermittedDestination(session: RetailerBootstrap): string {
  const enabledFlags = new Set(
    Object.entries(session.flags)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key),
  );
  const permissions = new Set(session.actor.permissions);
  const destination = RETAILER_ROUTES.find(
    (route) =>
      route.available &&
      route.navKey !== null &&
      enabledFlags.has(route.flag) &&
      route.permission !== null &&
      permissions.has(route.permission),
  );
  return retailerHref(destination?.path ?? '/');
}

function signedOutPublicPath(pathname: string): string {
  return pathname === retailerHref('/partner') ? pathname : retailerHref('/sign-in');
}

function RouteContent({
  pathname,
  client,
  onRequestStepUp,
}: {
  pathname: string;
  client: ReturnType<typeof createRetailerHttpClient>;
  onRequestStepUp: () => Promise<void>;
}) {
  const route = resolveRoute(pathname);
  // The loaded payload is tagged with the screen it was fetched for. A route
  // change renders before the loading effect runs, so an untagged state let the
  // incoming screen render with the previous screen's payload for one frame.
  // Screens that read a required field then threw and white-screened the whole
  // portal: navigating Imports (which loads no shared payload, so the value is
  // null) to Beauty After Hours crashed on `enrollment.locations`.
  const [state, setState] = useState<RemoteState<unknown>>({ status: 'loading' });
  const loadedScreen = state.status === 'loaded' ? state.screen : null;

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    const load = async () => {
      try {
        switch (route?.route.screen) {
          case 'Orders':
            return client.orders.list();
          case 'Order detail':
            return client.orders.detail(route.params.fulfilmentId ?? '');
          case 'Catalog':
            return client.catalog.list({ locationId: null, search: null });
          case 'Inventory':
            return client.inventory.health({
              locationId: null,
              stock: null,
              overnightOnly: false,
              categoryId: null,
              search: null,
              sort: 'updated',
            });
          case 'Payouts':
            return client.payouts.overview();
          case 'Dashboard':
            return client.dashboard.health();
          case 'Store profile':
            return client.store.read();
          case 'Beauty After Hours':
            return client.afterHours.read();
          default:
            return null;
        }
      } catch (error) {
        throw failure(error);
      }
    };
    const forScreen = route?.route.screen;
    void load().then(
      (value) => !cancelled && setState({ status: 'loaded', value, screen: forScreen }),
      (error) => !cancelled && setState({ status: 'failed', error: failure(error) }),
    );
    return () => {
      cancelled = true;
    };
  }, [client, pathname, route?.route.screen, route?.params.fulfilmentId]);

  // The failure state is checked first: it carries no screen tag, so testing the
  // tag before it would leave a failed load stuck on the loading state.
  if (state.status === 'failed') {
    return (
      <ErrorState
        headingLevel={1}
        title="Retailer data unavailable"
        description="We could not load retailer data. Try again shortly."
      />
    );
  }
  // A payload belonging to a different screen is not usable, so keep showing the
  // loading state until this screen's own fetch resolves.
  if (state.status === 'loading' || loadedScreen !== route?.route.screen)
    return <LoadingState label="Loading retailer data" />;
  switch (route?.route.screen) {
    case 'Orders':
      return (
        <OrdersScreen orders={Array.isArray(state.value) ? state.value.map(orderSummary) : []} />
      );
    case 'Order detail':
      return <OrderDetailScreen order={orderSummary(state.value)} client={client} />;
    case 'Catalog':
      return (
        <CatalogScreen
          listings={Array.isArray(state.value) ? state.value.map(record) : []}
          client={client}
        />
      );
    case 'Inventory':
      return (
        <InventoryScreen
          balances={Array.isArray(state.value) ? state.value.map(record) : []}
          client={client}
        />
      );
    case 'Dashboard':
      return <DashboardScreen dashboard={state.value as RetailerDashboard} />;
    case 'Store profile':
      return <StoreProfileScreen profile={state.value as RetailerStoreProfile} client={client} />;
    case 'Beauty After Hours':
      return <AfterHoursScreen enrollment={state.value as AfterHoursEnrollment} client={client} />;
    case 'Payouts':
      return <PayoutsScreen overview={state.value as RetailerPayoutOverview} />;
    case 'Security':
      return <RetailerSecurityScreen client={client.mfa} onRequestStepUp={onRequestStepUp} />;
    case 'Imports':
      return <ImportsScreen client={client} />;
    default:
      return null;
  }
}

export function BrowserRetailerApp() {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const challenge = useMfaStepUpChallenge();
  const client = useMemo(
    () => createRetailerHttpClient({ onMfaRequired: challenge.requestStepUp }),
    [challenge.requestStepUp],
  );
  const [session, setSession] = useState<RemoteState<RetailerBootstrap>>({ status: 'loading' });
  const [publicFlags, setPublicFlags] = useState<
    RemoteState<Readonly<Record<'retailer.lead_capture', boolean>>>
  >({ status: 'loading' });
  const [bootstrapVersion, setBootstrapVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void client.auth.bootstrap().then(
      (value) => !cancelled && setSession({ status: 'loaded', value }),
      (error) => !cancelled && setSession({ status: 'failed', error: failure(error) }),
    );
    return () => {
      cancelled = true;
    };
  }, [bootstrapVersion, client]);

  useEffect(() => {
    let cancelled = false;
    void client.auth.publicFlags().then(
      (value) => !cancelled && setPublicFlags({ status: 'loaded', value }),
      (error) => !cancelled && setPublicFlags({ status: 'failed', error: failure(error) }),
    );
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (session.status !== 'loaded' || pathname !== retailerHref('/sign-in')) return;
    const destination = firstPermittedDestination(session.value);
    window.history.replaceState({}, '', destination);
    setPathname(destination);
  }, [pathname, session]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (
        !(target instanceof HTMLAnchorElement) ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        target.target !== '' ||
        target.hasAttribute('download')
      )
        return;
      const url = new URL(target.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      event.preventDefault();
      window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
      setPathname(url.pathname);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const mfaDialog = (
    <MfaStepUpDialog
      client={client.mfa}
      open={challenge.open}
      onClose={challenge.close}
      onComplete={() => {
        challenge.complete();
        setBootstrapVersion((value) => value + 1);
      }}
    />
  );

  if (session.status === 'loading' || publicFlags.status === 'loading')
    return (
      <>
        <LoadingState label="Loading retailer portal" />
        {mfaDialog}
      </>
    );
  if (session.status === 'failed') {
    const signedOut =
      session.error instanceof RetailerApiError && session.error.code === 'UNAUTHENTICATED';
    if (!signedOut) {
      return (
        <ErrorState
          headingLevel={1}
          title="Retailer portal unavailable"
          description="We could not verify the retailer session. Try again shortly."
        />
      );
    }
  }
  const actor: RetailerActor =
    session.status === 'loaded'
      ? {
          signedIn: true,
          permissions: new Set(session.value.actor.permissions),
        }
      : { signedIn: false, permissions: new Set() };
  const flags =
    session.status === 'loaded'
      ? new Set(
          Object.entries(session.value.flags)
            .filter(([, enabled]) => enabled)
            .map(([key]) => key),
        )
      : new Set([
          'retailer.portal_auth',
          ...(publicFlags.status === 'loaded' && publicFlags.value['retailer.lead_capture']
            ? ['retailer.lead_capture']
            : []),
        ]);
  const appPath = session.status === 'failed' ? signedOutPublicPath(pathname) : pathname;

  return (
    <>
      <App
        path={appPath}
        client={client}
        flags={flags}
        actor={actor}
        renderScreen={() => (
          <RouteContent
            pathname={appPath}
            client={client}
            onRequestStepUp={challenge.requestStepUp}
          />
        )}
        onSignedIn={() => setBootstrapVersion((value) => value + 1)}
        onRequestStepUp={challenge.requestStepUp}
      />
      {mfaDialog}
    </>
  );
}
